import { type TeamWithCountsDto } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { MoreVertical, Pencil, Trash2, Users } from 'lucide-react';
import type { KeyboardEvent } from 'react';

import { resourceInteractiveCardClass } from '@/core/components/layout';

interface TeamCardProps {
  team: TeamWithCountsDto;
  canManage: boolean;
  onOpen: (team: TeamWithCountsDto) => void;
  onEdit: (team: TeamWithCountsDto) => void;
  onDelete: (team: TeamWithCountsDto) => void;
  dict: {
    memberCount: string;
    memberCountPlural: string;
    yourTeamBadge: string;
    edit: string;
    delete: string;
  };
}

export function TeamCard({
  team,
  canManage,
  onOpen,
  onEdit,
  onDelete,
  dict,
}: TeamCardProps) {
  const countTpl =
    team.memberCount === 1 ? dict.memberCount : dict.memberCountPlural;
  const memberCountLabel = countTpl.replace(
    '{count}',
    String(team.memberCount),
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(team);
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={team.name}
      className={cn(
        'group flex h-full cursor-pointer flex-col',
        resourceInteractiveCardClass,
      )}
      onClick={() => onOpen(team)}
      onKeyDown={handleKeyDown}
    >
      <CardContent className="flex h-full flex-1 flex-col gap-3 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <div
              aria-hidden
              className="size-6 shrink-0 rounded-full border border-border"
              style={{
                backgroundColor: team.color || 'var(--muted)',
              }}
            />
            <h3 className="truncate text-[15.5px] font-bold leading-tight text-foreground">
              {team.name}
            </h3>
          </div>

          {canManage ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-60 group-hover:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem onClick={() => onEdit(team)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {dict.edit}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(team)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {dict.delete}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        {team.description ? (
          <p className="line-clamp-2 text-[13.5px] leading-relaxed text-muted-foreground">
            {team.description}
          </p>
        ) : null}

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1 text-[13.5px] leading-relaxed text-muted-foreground">
          <Users className="h-4 w-4 shrink-0" />
          <span>{memberCountLabel}</span>
          {team.viewerIsMember ? (
            <Badge
              variant="outline"
              className="ml-1 font-normal text-muted-foreground"
            >
              {dict.yourTeamBadge}
            </Badge>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
